import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, NavLink } from 'react-router-dom';
import {
  BarChart3,
  Plus,
  User,
  Menu,
  X,
  ExternalLink,
  Shield,
  Key,
  BookOpen,
  LogOut,
  ChevronDown,
  Sparkles,
  Webhook,
  Users,
  CreditCard,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const navItems = [
  { to: '/create', label: 'Create', icon: Plus },
  { to: '/links', label: 'Links', icon: BarChart3 },
  { to: '/profile-pages', label: 'Profiles', icon: ExternalLink },
  { to: '/workspaces', label: 'Teams', icon: Users },
  { to: '/webhooks', label: 'Webhooks', icon: Webhook },
  { to: '/api-keys', label: 'API Keys', icon: Key },
  { to: '/billing', label: 'Billing', icon: CreditCard },
  { to: '/api-docs', label: 'Docs', icon: BookOpen },
];

const getInitials = (name = '') => {
  const parts = String(name).trim().split(/\s+|[._-]/).filter(Boolean);
  if (parts.length === 0) return 'U';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const BrandMark = () => (
  <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 via-brand-600 to-violet-600 text-white shadow-soft">
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className="h-4 w-4"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9.5 14.5l5-5M8 12a4 4 0 015.66 0l1.34 1.34a4 4 0 11-5.66 5.66l-1-1M16 12a4 4 0 01-5.66 0L9 10.66a4 4 0 115.66-5.66l1 1"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

const Navbar = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const userMenuRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsUserMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(e.target)
      ) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    setIsMobileMenuOpen(false);
    setIsUserMenuOpen(false);
  };

  const linkClass = ({ isActive }) =>
    [
      'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
      isActive
        ? 'bg-ink-100 text-ink-900'
        : 'text-ink-600 hover:text-ink-900 hover:bg-ink-100/70',
    ].join(' ');

  return (
    <header
      className={[
        'sticky top-0 z-40 w-full transition-all duration-200',
        scrolled
          ? 'bg-white/80 backdrop-blur-md border-b border-ink-200/80 shadow-soft'
          : 'bg-white/60 backdrop-blur-sm border-b border-transparent',
      ].join(' ')}
    >
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          {/* Brand */}
          <Link
            to="/"
            className="flex items-center gap-2.5 text-ink-900 group"
          >
            <BrandMark />
            <span className="text-base font-semibold tracking-tight">
              Dynalink
            </span>
          </Link>

          {/* Desktop nav */}
          {isAuthenticated ? (
            <nav className="hidden lg:flex items-center gap-0.5">
              {navItems.map((item) => (
                <NavLink key={item.to} to={item.to} className={linkClass}>
                  <item.icon className="h-4 w-4 opacity-80" />
                  <span>{item.label}</span>
                </NavLink>
              ))}
              {user?.role === 'admin' && (
                <NavLink
                  to="/admin"
                  className={({ isActive }) =>
                    [
                      'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-rose-50 text-rose-700'
                        : 'text-rose-600 hover:bg-rose-50',
                    ].join(' ')
                  }
                >
                  <Shield className="h-4 w-4" />
                  <span>Admin</span>
                </NavLink>
              )}
            </nav>
          ) : (
            <nav className="hidden md:flex items-center gap-1">
              <a
                href="#features"
                className="rounded-lg px-3 py-1.5 text-sm font-medium text-ink-600 hover:text-ink-900 hover:bg-ink-100/70"
              >
                Features
              </a>
              <Link
                to="/pricing"
                className="rounded-lg px-3 py-1.5 text-sm font-medium text-ink-600 hover:text-ink-900 hover:bg-ink-100/70"
              >
                Pricing
              </Link>
              <Link
                to="/api-docs"
                className="rounded-lg px-3 py-1.5 text-sm font-medium text-ink-600 hover:text-ink-900 hover:bg-ink-100/70"
              >
                Docs
              </Link>
            </nav>
          )}

          {/* Right side */}
          <div className="flex items-center gap-2">
            {isAuthenticated ? (
              <div className="relative hidden sm:block" ref={userMenuRef}>
                <button
                  onClick={() => setIsUserMenuOpen((v) => !v)}
                  className="inline-flex items-center gap-2 rounded-full border border-ink-200 bg-white pl-1 pr-2.5 py-1 text-sm font-medium text-ink-700 hover:bg-ink-50 transition"
                >
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-violet-600 text-white text-xs font-semibold">
                    {getInitials(user?.username)}
                  </span>
                  <span className="hidden md:inline max-w-[10ch] truncate">
                    {user?.username}
                  </span>
                  <ChevronDown className="h-4 w-4 text-ink-400" />
                </button>

                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 rounded-xl border border-ink-200 bg-white shadow-pop py-1.5 animate-scale-in origin-top-right">
                    <div className="px-3 py-2 border-b border-ink-100">
                      <p className="text-sm font-medium text-ink-900 truncate">
                        {user?.username}
                      </p>
                      {user?.email && (
                        <p className="text-xs text-ink-500 truncate">
                          {user.email}
                        </p>
                      )}
                    </div>
                    <Link
                      to="/profile"
                      className="flex items-center gap-2 px-3 py-2 text-sm text-ink-700 hover:bg-ink-50"
                    >
                      <User className="h-4 w-4 text-ink-400" />
                      Profile
                    </Link>
                    <Link
                      to="/api-keys"
                      className="flex items-center gap-2 px-3 py-2 text-sm text-ink-700 hover:bg-ink-50"
                    >
                      <Key className="h-4 w-4 text-ink-400" />
                      API Keys
                    </Link>
                    <Link
                      to="/billing"
                      className="flex items-center gap-2 px-3 py-2 text-sm text-ink-700 hover:bg-ink-50"
                    >
                      <CreditCard className="h-4 w-4 text-ink-400" />
                      Billing &amp; Plans
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="flex w-full items-center gap-2 px-3 py-2 text-sm text-rose-600 hover:bg-rose-50 border-t border-ink-100 mt-1"
                    >
                      <LogOut className="h-4 w-4" />
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Link
                  to="/login"
                  className="rounded-lg px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-100"
                >
                  Sign in
                </Link>
                <Link
                  to="/register"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-ink-900 px-3.5 py-1.5 text-sm font-medium text-white shadow-soft hover:bg-ink-800 transition"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  Get started
                </Link>
              </div>
            )}

            <button
              onClick={() => setIsMobileMenuOpen((v) => !v)}
              className="lg:hidden inline-flex h-9 w-9 items-center justify-center rounded-lg text-ink-700 hover:bg-ink-100 transition"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-ink-200 py-3 animate-fade-in">
            {isAuthenticated ? (
              <div className="space-y-1">
                <div className="flex items-center gap-3 px-2 py-3 mb-1 border-b border-ink-100">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-violet-600 text-white text-sm font-semibold">
                    {getInitials(user?.username)}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ink-900 truncate">
                      {user?.username}
                    </p>
                    {user?.email && (
                      <p className="text-xs text-ink-500 truncate">
                        {user.email}
                      </p>
                    )}
                  </div>
                </div>
                {navItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      [
                        'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium',
                        isActive
                          ? 'bg-ink-100 text-ink-900'
                          : 'text-ink-700 hover:bg-ink-100/70',
                      ].join(' ')
                    }
                  >
                    <item.icon className="h-4 w-4 text-ink-400" />
                    {item.label}
                  </NavLink>
                ))}
                {user?.role === 'admin' && (
                  <NavLink
                    to="/admin"
                    className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-rose-600 hover:bg-rose-50"
                  >
                    <Shield className="h-4 w-4" />
                    Admin
                  </NavLink>
                )}
                <NavLink
                  to="/profile"
                  className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-ink-700 hover:bg-ink-100/70"
                >
                  <User className="h-4 w-4 text-ink-400" />
                  Profile
                </NavLink>
                <button
                  onClick={handleLogout}
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-rose-600 hover:bg-rose-50"
                >
                  <LogOut className="h-4 w-4" />
                  Sign out
                </button>
              </div>
            ) : (
              <div className="space-y-1">
                <Link
                  to="/pricing"
                  className="block rounded-lg px-3 py-2.5 text-sm font-medium text-ink-700 hover:bg-ink-100/70"
                >
                  Pricing
                </Link>
                <Link
                  to="/login"
                  className="block rounded-lg px-3 py-2.5 text-sm font-medium text-ink-700 hover:bg-ink-100/70"
                >
                  Sign in
                </Link>
                <Link
                  to="/register"
                  className="block rounded-lg bg-ink-900 px-3 py-2.5 text-sm font-medium text-white text-center"
                >
                  Get started
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
